import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Chip,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import { 
  TrendingUp, 
  AccountBalance, 
  ExpandMore as ExpandMoreIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { TABS } from '../App';
import {
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle,
} from '@mui/icons-material';

const Header: React.FC = () => {
  const { state, logout, switchAccount } = useAppContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [switching, setSwitching] = useState(false);
  const open = Boolean(anchorEl);

  // Check if user is admin
  const isAdmin = state.googleUser?.role === 'admin';
  
  // Debug information for admin access issue
  // Admin access is working correctly

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleUserMenuClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavigateToAdmin = (tabIndex: number) => {
    handleUserMenuClose();
    // Find AppContent component and trigger tab change
    // This is a simple approach - in a production app you might use React Router
    const event = new CustomEvent('adminTabChange', { detail: tabIndex });
    window.dispatchEvent(event);
  };

  const handleSwitchAccount = async (accountId: string) => {
    if (accountId === state.currentAccountId || switching) {
      return;
    }

    try {
      setSwitching(true);
      await switchAccount(accountId);
      handleUserMenuClose();
    } catch (error) {
      console.error('Failed to switch account:', error);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <AppBar 
      position="static" 
      sx={{ 
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', py: 1, px: { xs: 1, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp sx={{ fontSize: { xs: 24, sm: 32 }, color: '#4caf50' }} />
            <Typography 
              variant={isMobile ? "h6" : "h5"}
              component="div" 
              sx={{ 
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #fff 30%, #e3f2fd 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1.1rem', sm: '1.5rem' }
              }}
            >
              {isMobile ? '股票系統' : '股票交易系統'}
            </Typography>
          </Box>
          
          {state.currentAccount && !isMobile && (
            <Chip
              icon={<AccountBalance />}
              label={`${state.currentAccount.name}: $${state.currentAccount.balance.toLocaleString()}`}
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '14px',
                border: '1px solid rgba(76, 175, 80, 0.4)',
                '& .MuiChip-icon': {
                  color: '#81c784',
                },
              }}
            />
          )}

          {state.currentAccount && isMobile && (
            <Chip
              icon={<AccountBalance sx={{ color: '#81c784 !important' }} />}
              label={state.currentAccount.name}
              size="small"
              title={`${state.currentAccount.accountType} • $${state.currentAccount.balance.toLocaleString()}`}
              sx={{
                backgroundColor: 'rgba(129, 199, 132, 0.2)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.8rem',
                height: 28,
                border: '1px solid rgba(129, 199, 132, 0.5)',
                '& .MuiChip-icon': {
                  fontSize: 18,
                },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {state.authUser ? (
            // User is logged in - show integrated user menu with account selector
            <>
              <Button
                onClick={handleUserMenuClick}
                variant="text"
                sx={{ 
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: { xs: 1, sm: 2 },
                  py: 1,
                  minWidth: 'auto',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
                startIcon={
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: state.googleUser?.role === 'admin' ? '#ffd54f' : '#4caf50',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {(state.currentUser?.name || state.authUser.displayName || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                }
                endIcon={
                  <ExpandMoreIcon 
                    sx={{ 
                      fontSize: 18, 
                      transition: 'transform 0.2s ease-in-out',
                      transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
                    }} 
                  />
                }
              >
                <Box sx={{ 
                  display: { xs: 'none', sm: 'flex' },
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                  ml: 1
                }}>
                  <Typography 
                    variant="body2" 
                    fontWeight="600" 
                    sx={{ 
                      color: 'white', 
                      fontSize: '0.875rem',
                      lineHeight: 1.2
                    }}
                  >
                    {state.currentUser?.name || state.authUser.displayName || '用戶'}
                  </Typography>
                  
                  {state.googleUser?.role === 'admin' ? (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#ffd54f', 
                        fontSize: '0.7rem',
                        fontWeight: '500'
                      }}
                    >
                      管理員
                    </Typography>
                  ) : (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255,255,255,0.7)', 
                        fontSize: '0.65rem'
                      }}
                    >
                      用戶
                    </Typography>
                  )}
                </Box>
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleUserMenuClose}
                PaperProps={{
                  sx: { 
                    minWidth: 280,
                    maxWidth: 320,
                    mt: 1
                  }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
              >
                <MenuItem sx={{ '&:hover': { backgroundColor: 'transparent' }, cursor: 'default' }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {state.currentUser?.name || state.authUser.displayName || '用戶'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {state.authUser.email}
                    </Typography>
                  </Box>
                </MenuItem>
                
                {/* Account Switching Section */}
                {state.googleUser && state.availableAccounts.length > 0 && [
                  <Divider key="account-divider" sx={{ my: 1 }} />,
                  <Box key="account-section-title" sx={{ px: 2, py: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      交易帳戶
                    </Typography>
                  </Box>,
                  ...state.availableAccounts.map((account) => (
                    <MenuItem
                      key={account.id}
                      onClick={() => handleSwitchAccount(account.id)}
                      disabled={switching}
                      sx={{
                        py: 1.5,
                        backgroundColor: account.id === state.currentAccountId ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      }}
                    >
                      <ListItemIcon>
                        {account.id === state.currentAccountId ? (
                          <CheckCircle color="primary" fontSize="small" />
                        ) : (
                          <Avatar sx={{ width: 24, height: 24, bgcolor: '#4caf50' }}>
                            <AccountBalance fontSize="small" />
                          </Avatar>
                        )}
                      </ListItemIcon>
                      <ListItemText>
                        <Typography variant="body2" fontWeight={account.id === state.currentAccountId ? 'bold' : 'normal'}>
                          {account.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.accountType} • ${account.balance.toLocaleString()}
                        </Typography>
                      </ListItemText>
                    </MenuItem>
                  ))
                ]}
                
                {/* Admin menu items */}
                {isAdmin && [
                  <Divider key="admin-divider" sx={{ my: 1 }} />,
                  <MenuItem key="account-management" onClick={() => handleNavigateToAdmin(TABS.ACCOUNT_MANAGEMENT)}>
                    <SettingsIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">帳戶管理</Typography>
                  </MenuItem>,
                  <MenuItem key="user-management" onClick={() => handleNavigateToAdmin(TABS.USER_MANAGEMENT)}>
                    <PeopleIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">用戶管理</Typography>
                  </MenuItem>
                ]}
                
                <Divider sx={{ my: 1 }} />
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">登出</Typography>
                </MenuItem>
              </Menu>
            </>
          ) : (
            // User is not logged in - show login button
            <Button 
              variant="outlined" 
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
              onClick={() => window.location.href = '/#/login'}
            >
              登入
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
